import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { IdCard, Camera, Clock, Upload } from "lucide-react";

const Picker = ({ label, hint, Icon, file, onPick, testid, capture }) => {
  const ref = useRef(null);
  return (
    <button type="button" data-testid={testid} onClick={() => ref.current?.click()}
      className={`relative flex-1 min-h-[8.5rem] rounded-2xl border border-dashed p-4 text-left transition-colors overflow-hidden ${file ? "border-gold/50" : "border-white/15 hover:border-white/30"}`}>
      <input ref={ref} type="file" accept="image/*" capture={capture} className="hidden" onChange={(e) => onPick(e.target.files?.[0] || null)} />
      {file && <img src={URL.createObjectURL(file)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
      <span className="relative block">
        <Icon size={20} className="text-gold" />
        <span className="block text-sm mt-2">{label}</span>
        <span className="block text-[11px] text-text-muted mt-1">{file ? "Tap to change" : hint}</span>
      </span>
    </button>
  );
};

export const ManualAgeCheck = ({ status, reason, onSubmitted }) => {
  const [idPhoto, setIdPhoto] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [busy, setBusy] = useState(false);

  if (status === "pending") {
    return (
      <div data-testid="manual-age-pending" className="mt-6 rounded-2xl border border-gold/30 bg-gold/5 p-5 flex gap-3">
        <Clock size={18} className="text-gold shrink-0 mt-0.5" />
        <p className="text-sm text-text-muted">Your ID and selfie are with the Velluto team. You'll get a message as soon as they're reviewed — usually within a day.</p>
      </div>
    );
  }

  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("id_photo", idPhoto);
      fd.append("selfie", selfie);
      await api.post("/verify/age-manual", fd);
      toast.success("Sent for review. We'll message you shortly.");
      onSubmitted();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed, please try again"); }
    setBusy(false);
  };

  return (
    <div data-testid="manual-age-check" className="mt-6 rounded-2xl border border-white/10 bg-ink/40 p-5">
      <p className="text-sm">Verify with our team instead</p>
      <p className="text-xs text-text-muted mt-1">Upload a photo of your ID and a selfie holding it. Only the Velluto team sees them, and they're deleted once reviewed.</p>
      {status === "rejected" && (
        <p data-testid="manual-age-rejected" className="mt-3 text-xs text-amber-300">Your last attempt wasn't approved{reason ? `: ${reason}` : ""}. Please try again.</p>
      )}
      <div className="flex flex-col sm:flex-row gap-3 mt-4">
        <Picker testid="manual-id-photo" label="Photo of your ID" hint="Passport, licence or ID card — date of birth visible" Icon={IdCard} file={idPhoto} onPick={setIdPhoto} capture="environment" />
        <Picker testid="manual-selfie" label="Selfie holding your ID" hint="Face and ID both clearly visible" Icon={Camera} file={selfie} onPick={setSelfie} capture="user" />
      </div>
      <button data-testid="manual-age-submit" onClick={submit} disabled={!idPhoto || !selfie || busy}
        className="mt-4 w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-wine hover:bg-wine-hover text-xs uppercase tracking-[0.2em] transition-colors disabled:opacity-40">
        <Upload size={14} /> {busy ? "Sending…" : "Send for review"}
      </button>
    </div>
  );
};
